import { useEffect, useState } from "react";
import axios from "axios";
import { TESTIMONIAL_TABS, TESTIMONIAL_NOTE } from "../../data/content";
import { FadeUp, SectionHead, ChapterTag } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function attribution(t, alias) {
    if (t.attribution_approved && t.name) {
        const main = [t.name, t.role].filter(Boolean).join(", ");
        const sub = [t.organisation, t.country].filter(Boolean).join(" · ");
        return { main, sub };
    }
    return { main: alias, sub: "" };
}

export default function Testimonials() {
    const [active, setActive] = useState("patient");
    const [items, setItems] = useState([]);

    useEffect(() => {
        axios
            .get(`${API}/testimonials`)
            .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
            .catch(() => setItems([]));
    }, []);

    const tab = TESTIMONIAL_TABS.find((t) => t.id === active);
    const list = items.filter((i) => i.category === active);

    return (
        <section id="testimonials" className="scroll-mt-20 bg-navy-surface py-24 lg:py-32" data-testid="testimonials-section">
            <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
                <ChapterTag>Chapter 10 &mdash; Testimonials</ChapterTag>
                <SectionHead
                    className="mt-8"
                    eyebrow="Genuine Voices"
                    title="Trusted by Patients and Partners"
                />

                <div className="mt-12 flex flex-wrap gap-2 border-b border-white/10" role="tablist" aria-label="Testimonial categories">
                    {TESTIMONIAL_TABS.map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            role="tab"
                            aria-selected={active === t.id}
                            data-testid={`testimonial-tab-${t.id}`}
                            onClick={() => setActive(t.id)}
                            className={`relative px-5 py-3.5 text-xs font-bold uppercase tracking-[0.16em] transition-colors duration-300 ${
                                active === t.id ? "text-gold" : "text-slate-400 hover:text-ivory"
                            }`}
                        >
                            {t.label}
                            <span
                                className={`absolute bottom-0 left-0 h-[2px] w-full origin-left bg-gold transition-transform duration-500 ${
                                    active === t.id ? "scale-x-100" : "scale-x-0"
                                }`}
                                aria-hidden="true"
                            />
                        </button>
                    ))}
                </div>

                <div className="mt-10" data-testid={`testimonial-panel-${active}`}>
                    {list.length === 0 ? (
                        <FadeUp>
                            <div className="border border-dashed border-white/12 p-10 lg:p-14">
                                <span className="text-[10px] font-semibold uppercase tracking-[0.32em] text-gold/70">
                                    Awaiting Verified Reviews
                                </span>
                                <p className="mt-5 max-w-2xl font-serif text-xl font-light italic leading-relaxed text-slate-300/85 lg:text-2xl">
                                    {tab.empty}
                                </p>
                            </div>
                        </FadeUp>
                    ) : (
                        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                            {list.map((t, i) => {
                                const by = attribution(t, tab.alias);
                                return (
                                    <FadeUp key={t.id} delay={(i % 3) * 0.08} className="h-full">
                                        <figure
                                            className="flex h-full flex-col border border-white/8 bg-navy-deep p-8"
                                            data-testid={`testimonial-card-${t.id}`}
                                        >
                                            <span className="font-serif text-5xl leading-none text-gold" aria-hidden="true">
                                                &ldquo;
                                            </span>
                                            <blockquote className="mt-4 text-base leading-relaxed text-slate-200/90">
                                                {t.text}
                                            </blockquote>
                                            <figcaption className="mt-auto pt-7">
                                                {t.attribution_approved && t.image_url && (
                                                    <img src={t.image_url} alt="" loading="lazy" className="mb-4 h-10 w-10 rounded-full object-cover" />
                                                )}
                                                <span className="block text-xs font-bold uppercase tracking-[0.2em] text-gold">
                                                    {by.main}
                                                </span>
                                                {by.sub && (
                                                    <span className="mt-1 block text-xs text-slate-500">{by.sub}</span>
                                                )}
                                                {t.created_at && (
                                                    <span className="mt-1 block text-[10px] uppercase tracking-[0.2em] text-slate-600">
                                                        {new Date(t.created_at).toLocaleDateString("en-GB", { month: "long", year: "numeric" })}
                                                    </span>
                                                )}
                                            </figcaption>
                                        </figure>
                                    </FadeUp>
                                );
                            })}
                        </div>
                    )}
                </div>

                <FadeUp delay={0.1}>
                    <p className="mt-10 text-xs leading-relaxed tracking-wide text-slate-500" data-testid="testimonial-note">
                        {TESTIMONIAL_NOTE}
                    </p>
                </FadeUp>
            </div>
        </section>
    );
}
